import { Button } from '@startuptools/ui/Button';
import { Box } from '@startuptools/ui/Box';
import { Table } from '@startuptools/ui/Table';
import { Snackbar } from '@startuptools/ui/Snackbar';
import TablePagination from '@mui/material/TablePagination';
import {
  type GqlParticipantsSummonsQuery,
  ParticipantsSummonsDocument,
  useDocGenerateMeetingSummonsDirectMutation,
} from './ParticipantsSummons.graphql';
import { Trans } from '@lingui/react';
import { isNil, toNumber } from 'lodash-es';
import { type GqlFileQuery, useSendMailMeetingSummonsToAllMutation } from '../../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { ParticipantSummonsRow } from './ParticipantSummonsRow';
import { useState } from 'react';
import { GqlDocGenState } from '../../../../graphql/base-types.graphql';
import { ShareholdersMeetingDocument } from '../../../shareholders-meetings/ShareholdersMeeting.graphql';
import { BoardMeetingDocument } from '../../../board-meetings/BoardMeeting.graphql';

export const ParticipantsSummonsTable = ({
  participants,
  summonsDoc,
  meetingId,
  totalCount,
  page,
  rowsPerPage,
  handlePageChange,
  handleRowsPerPageChange,
}: {
  participants: GqlParticipantsSummonsQuery['meetingParticipants']['models'];
  summonsDoc: GqlFileQuery['file'] | null | undefined;
  meetingId: string;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  handlePageChange: (newPage: number) => void;
  handleRowsPerPageChange: (rowsPerPage: number) => void;
}) => {
  const companyId = useCompanyId();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [doSendToAll, { loading: sendLoading }] = useSendMailMeetingSummonsToAllMutation({
    refetchQueries: [ShareholdersMeetingDocument, BoardMeetingDocument, ParticipantsSummonsDocument],
  });
  const [doGenerate, { loading: generateLoading }] = useDocGenerateMeetingSummonsDirectMutation({
    variables: { companyId, meetingId },
    awaitRefetchQueries: true,
  });

  const handleSendToAll = async () => {
    let documentId = summonsDoc?.id;
    let genState = summonsDoc?.genState;
    if (!summonsDoc || summonsDoc.dirty || summonsDoc.genState === GqlDocGenState.Failed) {
      const { data } = await doGenerate();
      if (!data) {
        return;
      }
      documentId = data.docGenerateMeetingSummonsDirect.id;
      genState = data.docGenerateMeetingSummonsDirect.genState;
    }
    if (!isNil(genState) && genState !== GqlDocGenState.Completed) {
      // Toast to have them wait for generation
      setShowSnackbar(true);
      return;
    }
    if (!documentId) {
      throw new Error('documentId undefined when it should not be');
    }
    await doSendToAll({ variables: { companyId, input: { meetingId, fileId: documentId } } });
  };

  const loading = sendLoading || generateLoading;

  return (
    <>
      <Table size="lg">
        <thead>
          <tr>
            <th>
              <Trans id="Participant" />
            </th>
            <th>
              <Trans id="Representative" />
            </th>
            <th>
              <Trans id="Email" />
            </th>
            <th>
              <Trans id="Email Status" />
            </th>
            <th className="actions-col">
              <Box sx={{ textAlign: 'right' }}>
                <Button variant="outlined" onClick={handleSendToAll} loading={loading}>
                  <Trans id="Send to all" />
                </Button>
              </Box>
            </th>
          </tr>
        </thead>
        <tbody>
          {participants.map(p => {
            return (
              <ParticipantSummonsRow
                key={p.id}
                participant={p}
                summonsDoc={summonsDoc}
                meetingId={meetingId}
                loading={loading}
                doGenerate={doGenerate}
                setShowSnackbar={setShowSnackbar}
              />
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <TablePagination
              colSpan={5}
              count={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_, newPage) => handlePageChange(newPage)}
              onRowsPerPageChange={event => handleRowsPerPageChange(toNumber(event.target.value))}
              labelRowsPerPage={<Trans id="Rows per page:" />}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </tr>
        </tfoot>
      </Table>
      <Snackbar open={showSnackbar} onClose={() => setShowSnackbar(false)}>
        <Trans id="Wait for the summons PDF to finish generating" />
      </Snackbar>
    </>
  );
};
